import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowDown as DownloadIcon,
  FaRegCopy as CopyIcon,
} from 'react-icons/fa';
import { Button, TableCell, TableRow, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';

import { SECTION } from 'src/constants/section';
import settings from 'src/settings';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { ActionPlan } from 'src/types/planAction';
import { getActionPlanStatus } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import { CHECKBOX_MODE } from 'src/components/common/OrgTree/types';
import type { OnSelectProps } from 'src/components/common/OrgUnitModal';
import OrgUnitModal from 'src/components/common/OrgUnitModal';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';

import { styles } from './index.styles';

export const ACTION_PLAN_TABLE_ROW_TEST_ID = 'action-plan-table-row';
export const ACTION_PLAN_COPY_TEST_ID = 'action-plan-copy';
export const ACTION_PLAN_DOWNLOAD_TEST_ID = 'action-plan-download';

export interface PlanActionTableRowProps {
  actionPlan: ActionPlan;
  childOrgUnits: ModuleOrPlanActionOrgUnit[];
  onDownloadClick: (id: number) => void;
}

const PlanActionTableRow: FC<PlanActionTableRowProps> = ({
  actionPlan,
  childOrgUnits,
  onDownloadClick,
}) => {
  const { t } = useTranslation('planAction');
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const moduleId = SECTION.PLAN_ACTION;
  const { fetchActionPlansForSelectedOrgUnit } = usePlanActionContext();
  const filePath = `${settings.VITE_APP_API_URL}${apiPaths.PLAN_ACTION.ACTION_PLAN}${actionPlan.id}/download_pdf/`;

  const copyActionPlan = ({ filterGroups }: OnSelectProps) => {
    const orgUnitIds = filterGroups.orgUnit.map((unit) =>
      Number(unit.filterExpr.orgUnitDbId),
    );

    const payload = {
      action_plan_id: actionPlan.id,
      org_unit_ids: orgUnitIds,
    };

    HttpService.post(apiPaths.PLAN_ACTION.COPY_ACTION_PLAN, payload)
      .then((response) => {
        // Fetch all actionPlans from all the selected OrgUnits.
        orgUnitIds.map((orgUnitId: number) =>
          fetchActionPlansForSelectedOrgUnit(orgUnitId),
        );

        enqueueSnackbar(t('SuccessfulCopied'), {
          variant: 'success',
        });

        return response;
      })
      .catch((error) => {
        enqueueSnackbar(t('NotSuccessfulCopied'), {
          variant: 'error',
        });

        return error;
      });
  };

  return (
    <>
      <TableRow
        style={styles.tableRow}
        onClick={() =>
          history.push(`${routePaths.PLAN_ACTION_OVERVIEW}${actionPlan.id}`)
        }
        data-test-id={ACTION_PLAN_TABLE_ROW_TEST_ID}
      >
        <TableCell sx={styles.tableCell} component="th" scope="row">
          <span>{actionPlan.name}</span>
        </TableCell>

        <TableCell sx={styles.tableCell}>
          <span>{actionPlan.creator}</span>
        </TableCell>

        <TableCell sx={styles.tableCell}>
          <span>
            {t(`ActionPlan.Status.${getActionPlanStatus(actionPlan.status)}`)}
          </span>
        </TableCell>

        <TableCell sx={styles.tableCell} align="right">
          <Button
            onClick={(event) => {
              setShowModal(true);
              event.stopPropagation();
            }}
            variant="contained"
            style={styles.button}
            data-test-id={ACTION_PLAN_COPY_TEST_ID}
          >
            <CopyIcon />
          </Button>
        </TableCell>

        <TableCell sx={styles.tableCell} align="right">
          <Tooltip placement="top" title={t('ActionPlan.DownloadActionPlan')}>
            <Button
              disabled={!filePath}
              download
              href={filePath}
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                actionPlan.id && onDownloadClick(actionPlan.id);
                event.stopPropagation();
              }}
              size="small"
              target="_blank"
              variant="contained"
              style={styles.button}
              data-test-id={ACTION_PLAN_DOWNLOAD_TEST_ID}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>

      <FilterProvider>
        <OrgUnitModal
          checkboxMode={CHECKBOX_MODE.MULTI}
          moduleId={moduleId}
          onClose={() => setShowModal(false)}
          onSelect={copyActionPlan}
          orgUnits={childOrgUnits}
          showModal={showModal}
          subTitle={t('ModalText')}
        />
      </FilterProvider>
    </>
  );
};

export default PlanActionTableRow;
